// store/modules/forms.js

/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from "firebase/firestore";

export default {
  namespaced: true,

  state: {
    // Form definitions
    formDefinitions: {},
    // Active form
    currentFormId: null,
    // Submission results
    submissions: {},
    // Loading states
    loading: {
      forms: false,
      submission: false,
      definitions: false
    },
    // Error states
    errors: {
      forms: null,
      submission: null,
      definitions: null
    }
  },

  getters: {
    /**
     * Get the current form definition
     */
    currentForm: state => {
      return state.currentFormId
        ? state.formDefinitions[state.currentFormId]
        : null;
    },

    /**
     * Get a specific form's definition by ID
     */
    getFormById: state => id => {
      return state.formDefinitions[id] || null;
    },

    /**
     * Get all form field definitions as a flattened array
     */
    getFormFieldsFlattened: state => formId => {
      const form = state.formDefinitions[formId];
      if (!form) return [];

      const fields = [];

      // Helper function to recursively extract fields
      const extractFields = items => {
        if (!Array.isArray(items)) return;

        items.forEach(item => {
          fields.push(item);

          // Handle nested fields in sections
          if (item.type === "section" && Array.isArray(item.fields)) {
            extractFields(item.fields);
          }

          // Handle child fields
          if (Array.isArray(item.children)) {
            extractFields(item.children);
          }
        });
      };

      if (form.sections) {
        extractFields(form.sections.flatMap(section => section.fields || []));
      }

      return fields;
    },

    /**
     * Get all available form definitions
     */
    allFormDefinitions: state => {
      return state.formDefinitions;
    },

    /**
     * Get form definitions as options array for dropdowns
     */
    formDefinitionsOptions: state => {
      return Object.entries(state.formDefinitions).map(([id, form]) => ({
        text: form.title || form.name || id,
        value: id
      }));
    },

    /**
     * Get loading state for a specific key
     */
    formsIsLoading: state => key => {
      return state.loading[key] || false;
    },

    /**
     * Get error for a specific key
     */
    getError: state => key => {
      return state.errors[key];
    },

    /**
     * Get submission by form ID
     */
    getSubmissionByFormId: state => formId => {
      return state.submissions[formId] || null;
    },

    /**
     * Get all submissions
     */
    allSubmissions: state => {
      return state.submissions;
    },

    /**
     * Check if form has been submitted
     */
    isFormSubmitted: state => formId => {
      return !!state.submissions[formId];
    },

    /**
     * Get forms count
     */
    formsCount: state => {
      return Object.keys(state.formDefinitions).length;
    },

    /**
     * Get submissions count
     */
    submissionsCount: state => {
      return Object.keys(state.submissions).length;
    }
  },

  mutations: {
    setFormDefinitions(state, definitions) {
      state.formDefinitions = { ...definitions };
    },

    setFormDefinition(state, { id, definition }) {
      state.formDefinitions = {
        ...state.formDefinitions,
        [id]: definition
      };
    },

    removeFormDefinition(state, formId) {
      const { [formId]: removed, ...remaining } = state.formDefinitions;
      state.formDefinitions = remaining;
    },

    setCurrentForm(state, formId) {
      state.currentFormId = formId;
    },

    setLoading(state, { key, value }) {
      state.loading = {
        ...state.loading,
        [key]: Boolean(value)
      };
    },

    setError(state, { key, error }) {
      state.errors = {
        ...state.errors,
        [key]: error
      };
    },

    clearError(state, key) {
      state.errors = {
        ...state.errors,
        [key]: null
      };
    },

    clearAllErrors(state) {
      state.errors = {
        forms: null,
        submission: null,
        definitions: null
      };
    },

    saveSubmission(state, { formId, submission }) {
      state.submissions = {
        ...state.submissions,
        [formId]: submission
      };
    },

    removeSubmission(state, formId) {
      const { [formId]: removed, ...remaining } = state.submissions;
      state.submissions = remaining;
    },

    clearAllSubmissions(state) {
      state.submissions = {};
    }
  },

  actions: {
    /**
     * Load form definitions from Firebase
     */
    async loadFormDefinitions({ commit, getters }) {
      commit("setLoading", { key: "definitions", value: true });
      commit("clearError", "definitions");

      try {
        const programRef = doc(db(), "programs", getters.programId);
        const formsRef = collection(programRef, "forms");
        const formsQuery = query(formsRef, orderBy("title"));
        const querySnapshot = await getDocs(formsQuery);

        const definitions = {};
        querySnapshot.forEach(docSnapshot => {
          const data = docSnapshot.data();
          definitions[docSnapshot.id] = {
            id: docSnapshot.id,
            ...data,
            created: data.created?.toDate() || new Date(),
            updated: data.updated?.toDate() || new Date()
          };
        });

        commit("setFormDefinitions", definitions);
        return definitions;
      } catch (error) {
        console.error("Error loading form definitions:", error);
        commit("setError", { key: "definitions", error: error.message });
        throw error;
      } finally {
        commit("setLoading", { key: "definitions", value: false });
      }
    },

    /**
     * Get form definition by ID from Firebase
     */
    async getFormDefinitionById({ commit, getters }, formId) {
      if (!formId) {
        console.error("Form ID is required");
        return null;
      }

      try {
        const programRef = doc(db(), "programs", getters.programId);
        const formRef = doc(collection(programRef, "forms"), formId);
        const formDoc = await getDoc(formRef);

        if (formDoc.exists()) {
          const data = formDoc.data();
          const definition = {
            id: formDoc.id,
            ...data,
            created: data.created?.toDate() || new Date(),
            updated: data.updated?.toDate() || new Date()
          };

          commit("setFormDefinition", { id: formId, definition });
          return definition;
        } else {
          console.warn(`Form definition with ID ${formId} not found`);
          return null;
        }
      } catch (error) {
        console.error("Error getting form definition by ID:", error);
        return null;
      }
    },

    /**
     * Create a new form definition
     */
    async createFormDefinition({ commit, getters, dispatch }, payload) {
      if (!payload || !payload.title) {
        throw new Error("Form definition with title is required");
      }

      commit("setLoading", { key: "forms", value: true });
      commit("clearError", "forms");

      try {
        const programRef = doc(db(), "programs", getters.programId);
        const formsRef = collection(programRef, "forms");

        // Check for duplicate titles
        const titleUppercase = payload.title.toUpperCase();
        const titleDupQuery = query(
          formsRef,
          where("titleUppercase", "==", titleUppercase)
        );
        const titleDupSnapshot = await getDocs(titleDupQuery);

        if (!titleDupSnapshot.empty) {
          throw new Error("Form title already exists");
        }

        const formData = {
          ...payload,
          titleUppercase: titleUppercase,
          created: serverTimestamp(),
          updated: serverTimestamp(),
          createdBy: getters.user?.id || "unknown",
          updatedBy: getters.user?.id || "unknown"
        };

        const newFormRef = await addDoc(formsRef, formData);

        const tempForm = {
          ...payload,
          id: newFormRef.id,
          titleUppercase: titleUppercase,
          created: new Date(),
          updated: new Date(),
          createdBy: getters.user?.id || "unknown",
          updatedBy: getters.user?.id || "unknown"
        };

        commit("setFormDefinition", {
          id: newFormRef.id,
          definition: tempForm
        });
        dispatch("setSnackbar", "Form definition created successfully");

        return tempForm;
      } catch (error) {
        console.error("Error creating form definition:", error);
        commit("setError", { key: "forms", error: error.message });
        throw error;
      } finally {
        commit("setLoading", { key: "forms", value: false });
      }
    },

    /**
     * Update form definition
     */
    async updateFormDefinition({ commit, getters, dispatch }, { id, updates }) {
      if (!id || !updates) {
        throw new Error("Form ID and updates are required");
      }

      commit("setLoading", { key: "forms", value: true });
      commit("clearError", "forms");

      try {
        const programRef = doc(db(), "programs", getters.programId);
        const formRef = doc(collection(programRef, "forms"), id);

        // Verify form exists
        const formDoc = await getDoc(formRef);
        if (!formDoc.exists()) {
          throw new Error("Form definition not found");
        }

        // Check for duplicate titles if title is being updated
        if (updates.title) {
          const titleUppercase = updates.title.toUpperCase();
          const formsRef = collection(programRef, "forms");
          const titleDupQuery = query(
            formsRef,
            where("titleUppercase", "==", titleUppercase)
          );
          const titleDupSnapshot = await getDocs(titleDupQuery);

          if (!titleDupSnapshot.empty) {
            let duplicated = false;
            titleDupSnapshot.forEach(docSnapshot => {
              if (docSnapshot.id !== id) {
                duplicated = true;
              }
            });
            if (duplicated) {
              throw new Error("Form title already exists");
            }
          }
          updates.titleUppercase = titleUppercase;
        }

        const updateData = {
          ...updates,
          updated: serverTimestamp(),
          updatedBy: getters.user?.id || "unknown"
        };

        await updateDoc(formRef, updateData);

        const updatedForm = {
          ...formDoc.data(),
          ...updates,
          updated: new Date(),
          updatedBy: getters.user?.id || "unknown"
        };

        commit("setFormDefinition", { id, definition: updatedForm });
        dispatch("setSnackbar", "Form definition updated successfully");

        return updatedForm;
      } catch (error) {
        console.error("Error updating form definition:", error);
        commit("setError", { key: "forms", error: error.message });
        throw error;
      } finally {
        commit("setLoading", { key: "forms", value: false });
      }
    },

    /**
     * Delete form definition
     */
    async deleteFormDefinition({ commit, getters, dispatch }, formId) {
      if (!formId) {
        throw new Error("Form ID is required");
      }

      try {
        const programRef = doc(db(), "programs", getters.programId);
        const formRef = doc(collection(programRef, "forms"), formId);

        await deleteDoc(formRef);

        commit("removeFormDefinition", formId);
        dispatch("setSnackbar", "Form definition deleted successfully");

        return true;
      } catch (error) {
        console.error("Error deleting form definition:", error);
        throw error;
      }
    },

    /**
     * Set the current active form
     */
    setCurrentForm({ commit }, formId) {
      commit("setCurrentForm", formId);
    },

    /**
     * Submit form data to Firebase
     */
    async submitFormData({ commit, getters, dispatch }, { formId, formData }) {
      if (!formId) {
        throw new Error("Form ID is required");
      }

      if (!formData) {
        throw new Error("Form data is required");
      }

      commit("setLoading", { key: "submission", value: true });
      commit("clearError", "submission");

      try {
        const programRef = doc(db(), "programs", getters.programId);
        const submissionsRef = collection(
          doc(collection(programRef, "forms"), formId),
          "submissions"
        );

        const submissionData = {
          formId,
          data: formData,
          submittedAt: serverTimestamp(),
          submittedBy: getters.user?.id || "anonymous",
          status: "submitted"
        };

        const submissionRef = await addDoc(submissionsRef, submissionData);

        const response = {
          success: true,
          message: "Form submitted successfully",
          id: submissionRef.id
        };

        // Save the submission result
        const submission = {
          id: submissionRef.id,
          data: formData,
          response,
          timestamp: new Date().toISOString(),
          submittedBy: getters.user?.id || "anonymous"
        };

        commit("saveSubmission", { formId, submission });
        dispatch("setSnackbar", "Form submitted successfully");

        return response;
      } catch (error) {
        console.error("Error submitting form:", error);
        commit("setError", { key: "submission", error: error.message });
        throw error;
      } finally {
        commit("setLoading", { key: "submission", value: false });
      }
    },

    /**
     * Load form submissions
     */
    async loadFormSubmissions({ commit, getters }, formId) {
      if (!formId) {
        console.error("Form ID is required");
        return [];
      }

      try {
        const programRef = doc(db(), "programs", getters.programId);
        const submissionsRef = collection(
          doc(collection(programRef, "forms"), formId),
          "submissions"
        );
        const submissionsQuery = query(
          submissionsRef,
          orderBy("submittedAt", "desc")
        );
        const querySnapshot = await getDocs(submissionsQuery);

        const submissions = [];
        querySnapshot.forEach(docSnapshot => {
          const data = docSnapshot.data();
          submissions.push({
            id: docSnapshot.id,
            ...data,
            submittedAt: data.submittedAt?.toDate() || new Date()
          });
        });

        return submissions;
      } catch (error) {
        console.error("Error loading form submissions:", error);
        return [];
      }
    },

    /**
     * Clear current form
     */
    clearCurrentForm({ commit }) {
      commit("setCurrentForm", null);
    },

    /**
     * Clear specific submission
     */
    clearSubmission({ commit }, formId) {
      commit("removeSubmission", formId);
    },

    /**
     * Clear all submissions
     */
    clearAllSubmissions({ commit }) {
      commit("clearAllSubmissions");
    },

    /**
     * Clear specific error
     */
    clearError({ commit }, key) {
      commit("clearError", key);
    },

    /**
     * Clear all errors
     */
    clearAllErrors({ commit }) {
      commit("clearAllErrors");
    }
  }
};
